import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import useDeviceType from '../../../providers/DeviceType/hooks';
import ComponentType from '../../../types/Component';

import TooltipPopover from './TooltipPopover';
import TooltipPortal from './TooltipPortal';

export type CoordsType = {
	left: number;
	top: number;
};

type PortalType = {
	tooltipLabel: ReactNode;
	destroyOnInteract?: boolean;
	parentNodeSelector?: string;
};

const Portal: ComponentType<PortalType> = ({
	children,
	tooltipLabel,
	destroyOnInteract,
	parentNodeSelector,
}) => {
	const portalRef = useRef<HTMLDivElement>(null);
	const [isOn, setOn] = useState<boolean>(false);
	const { isDesktop, isMobile } = useDeviceType();
	const [coords, setCoords] = useState<CoordsType>({ left: 0, top: 0 });

	const updateTooltipCoords = useCallback(() => {
		if (!portalRef.current) return;
		const rect = portalRef.current.getBoundingClientRect();
		setCoords({
			left: rect.x + rect.width / 2,
			top: rect.y + window.scrollY,
		});
	}, []);

	const handleUpdateOnOutsideInteraction = useCallback(() => {
		if (destroyOnInteract) {
			setOn(false);
			return;
		}
		updateTooltipCoords();
	}, [updateTooltipCoords, destroyOnInteract]);

	const onMouseEnter = useCallback(
		(event: React.MouseEvent | React.TouchEvent) => {
			event.stopPropagation();
			event.nativeEvent.stopImmediatePropagation();
			event.nativeEvent.stopPropagation();

			updateTooltipCoords();
			setOn((prev) => !prev);
		},
		[isOn]
	);

	const handleHidePortal = useCallback(() => setOn(false), []);

	useEffect(() => {
		if (destroyOnInteract && isMobile) {
			window.addEventListener('touchstart', handleHidePortal, false);
		}
		return () => {
			window.removeEventListener('touchstart', handleHidePortal, false);
		};
	}, [destroyOnInteract, isMobile]);

	return (
		<>
			<div
				style={{ cursor: 'pointer' }}
				ref={portalRef}
				onTouchStart={isMobile ? onMouseEnter : undefined}
				onMouseEnter={isDesktop ? onMouseEnter : undefined}
				onMouseLeave={isDesktop ? handleHidePortal : undefined}>
				{tooltipLabel}
			</div>
			{isOn && (
				<TooltipPortal>
					<TooltipPopover
						coords={coords}
						nodeSelectorToTrack={parentNodeSelector}
						updateTooltipCoords={handleUpdateOnOutsideInteraction}>
						<div>{children}</div>
					</TooltipPopover>
				</TooltipPortal>
			)}
		</>
	);
};

export default Portal;
